import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../css/UnitList.css';
import '../css/ProductList.css';


const ProductList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const API_URL = process.env.REACT_APP_API_URL;

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => {
      setToast({ show: false, message: '', type: '' });
    }, 3000);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(`${API_URL}product-list.php`);
        const data = await response.json();
        if (data.status && Array.isArray(data.data)) {
          // Map API fields to local fields
          const mappedProducts = data.data.map(item => ({
            id: item.id,
            name: item.product,
            unit: item.unit,
            quantity: item.quantity, // <-- Add this line
            createdAt: item.create_at,
          }));
          setProducts(mappedProducts);
        } else {
          showToast('Failed to fetch products.', 'error');
        }
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching products:', error);
        showToast('Error loading products. Please try again.', 'error');
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.unit.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const handleEdit = (product) => {
    navigate('/add-product', { state: { product: { id: product.id, name: product.name }, isEditing: true } });
    showToast(`Editing product: ${product.name}`, 'info');
  };

  const handleDelete = async (productId) => {
    try {
      const formData = new FormData();
      formData.append('id', productId);
      const response = await fetch(`${API_URL}product-delete.php`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if ((data.message && data.message.includes('deleted successfully')) || data.status === 'success') {
        setProducts(products.filter(product => product.id !== productId));
        showToast(`Product "${productToDelete.name}" deleted successfully`, 'success');
      } else {
        showToast(data.message || 'Failed to delete product.', 'error');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      showToast('Error deleting product. Please try again.', 'error');
    }
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  const openDeleteModal = (product) => {
    setProductToDelete(product);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setProductToDelete(null);
  };

  const handleAddProduct = () => {
    navigate('/add-product');
    showToast('Navigating to add new product', 'info');
  };

  if (isLoading) {
    return (
      <div className="menu-list-content">
        <div style={{ textAlign: 'center', margin: '2rem' }}>Loading products...</div>
      </div>
    );
  }

  return (
    <div className="menu-list-content">
      <div className="page-header">
        <div className="header-content">
          <h1>Product Management</h1>
          <p>Manage your inventory products</p>
        </div>
        <button className="add-unit-button" onClick={handleAddProduct}>
          + Add New Product
        </button>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search products by name or unit..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="unit-list-table-container">
        <table className="unit-list-table">
          <thead>
            <tr>
              <th>Photo</th>
              <th>Product Name</th>
              <th>Unit</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.map(product => (
              <tr key={product.id}>
                <td>
                  <div className="unit-icon">📦</div>
                </td>
                <td>{product.name}</td>
                <td>{product.quantity} {product.unit}</td>
                <td>{product.createdAt ? product.createdAt.slice(0, 10) : ''}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="action-btn edit"
                      onClick={() => handleEdit(product)}
                    >
                      Edit
                    </button>
                    <button 
                      className="action-btn delete"
                      onClick={() => openDeleteModal(product)}
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-footer">
        <div className="results-info">
          Showing {filteredProducts.length} of {products.length} products
        </div>
      </div>

      {toast.show && (
        <div className={`toast toast-${toast.type}`}>
          <div className="toast-content">
            <span className="toast-message">{toast.message}</span>
            <button 
              className="toast-close" 
              onClick={() => setToast({ show: false, message: '', type: '' })}
            >
              ×
            </button>
          </div>
        </div>
      )}
      {showDeleteModal && productToDelete && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>Confirm Delete</h2>
            <p>Are you sure you want to delete <b>{productToDelete.name}</b>?</p>
            <div className="modal-actions">
              <button className="modal-btn confirm" onClick={() => handleDelete(productToDelete.id)}>Yes, Delete</button>
              <button className="modal-btn cancel" onClick={closeDeleteModal}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductList; 